import { Link } from 'react-router-dom'
import { Shell } from '../components/Layout'
import { useStore } from '../lib/store'
import { COMPANY, LEGAL_UPDATED, SOCIALS } from '../lib/site'

/**
 * Terms & Conditions and the Privacy Policy.
 *
 * Both are written from what this store actually does — the returns window and
 * delivery times in the FAQs, the tables in `worker/db.js`, the session cookie
 * in `worker/auth.js`, Stripe for payment. Change any of those and the matching
 * clause here needs changing too, or the page is quietly untrue.
 */

/* ------------------------------------------------------------------ layout */

function LegalShell({
  title,
  summary,
  sections,
  children,
}: {
  title: string
  summary: string
  sections: string[]
  children: React.ReactNode
}) {
  return (
    <Shell>
      <section className="section">
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="eyebrow">Legal</div>
          <h1 className="display h-lg">{title}</h1>
          <p className="small" style={{ margin: 0 }}>
            Last updated {LEGAL_UPDATED}
          </p>
        </div>

        <div className="legal" style={{ marginTop: 'clamp(26px,3vw,42px)' }}>
          <p style={{ fontSize: 14 }}>{summary}</p>

          <nav className="legal-toc" aria-label="On this page">
            <ol>
              {sections.map((s, i) => (
                <li key={s}>
                  <a href={`#s${i + 1}`}>{s}</a>
                </li>
              ))}
            </ol>
          </nav>

          {children}
        </div>
      </section>
    </Shell>
  )
}

/** One numbered clause. `n` drives both the heading and the anchor the contents list points at. */
function Clause({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <>
      <h2 id={`s${n}`}>
        <span className="legal-n">{String(n).padStart(2, '0')}</span>
        {title}
      </h2>
      {children}
    </>
  )
}

const Mail = () => <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>

/** The registered details, printing only the ones someone has actually filled in. */
function CompanyDetails() {
  return (
    <ul>
      <li>
        <strong>{COMPANY.legalName}</strong>, trading as {COMPANY.tradingName}
      </li>
      {COMPANY.companyNumber && <li>Registered in England and Wales, company number {COMPANY.companyNumber}</li>}
      {COMPANY.registeredAddress && <li>Registered address: {COMPANY.registeredAddress}</li>}
      <li>
        Email: <Mail />
      </li>
    </ul>
  )
}

/* ------------------------------------------------------------------- terms */

const TERMS_SECTIONS = [
  'Who we are',
  'About these terms',
  'Placing an order',
  'Prices and payment',
  'Delivery',
  'Changing your mind',
  'If something is wrong',
  'Using our products safely',
  'Your account',
  'Reviews and anything you post',
  'Our content',
  'Availability of this site',
  'Our responsibility to you',
  'Things outside our control',
  'Changes to these terms',
  'Law and where to complain',
]

export function Terms() {
  const { settings, money } = useStore()
  const freeOver = settings ? money(settings.freeShippingThreshold) : 'the threshold shown at checkout'

  return (
    <LegalShell
      title="Terms & Conditions"
      summary="These terms cover buying from lumeracosmeticsgroup.com. They are written to be read rather than skimmed past, and nothing in them takes away the rights UK consumer law already gives you."
      sections={TERMS_SECTIONS}
    >
      <Clause n={1} title="Who we are">
        <CompanyDetails />
      </Clause>

      <Clause n={2} title="About these terms">
        <p>
          These terms apply when you buy anything from this site or use it as a visitor. By placing an order you agree to them. If you do
          not, please do not order — nothing else on the site asks you to accept anything.
        </p>
        <p>
          They are written for consumers buying for personal use. If you are buying to resell, please contact us at <Mail /> rather than
          ordering through the site.
        </p>
      </Clause>

      <Clause n={3} title="Placing an order">
        <p>
          Adding something to your bag is not an order, and neither is reaching the checkout page. When you submit the checkout form you
          are offering to buy. We accept that offer when we confirm your order, and the contract between us starts at that moment.
        </p>
        <p>
          Every order is priced on our server from our own product records, not from anything your browser sends. If a price shown to you
          was wrong, we will contact you before dispatch and you can confirm at the corrected price or cancel for a full refund. We will
          never simply charge you more than the page said.
        </p>
        <p>
          We may decline an order — if an item sells out between your bag and your payment, if we cannot deliver to your address, or if we
          reasonably suspect fraud. If we decline after taking payment, you are refunded in full.
        </p>
      </Clause>

      <Clause n={4} title="Prices and payment">
        <p>
          Prices are in {settings?.currency ?? 'GBP'} and include VAT where it applies. Delivery is shown separately at checkout before you
          pay, and is free on orders over {freeOver}.
        </p>
        <p>
          Payment is taken by{' '}
          <a href="https://stripe.com" target="_blank" rel="noreferrer noopener">
            Stripe
          </a>{' '}
          on their own secure payment page. Your card details are entered there and go to Stripe, not to us — we never see, handle or store
          your card number. We receive only a confirmation that payment succeeded and a reference to look it up.
        </p>
        <p>Your items are reserved while you pay. If you abandon payment that reservation is released and no order is placed.</p>
      </Clause>

      <Clause n={5} title="Delivery">
        <p>
          UK standard delivery takes two to three working days. International delivery takes five to ten working days depending on the
          destination, with any duties calculated at checkout so nothing arrives as a surprise.
        </p>
        <p>
          These are estimates rather than guarantees. If delivery has not arrived within thirty days of your order, and we have not agreed a
          longer time with you, you can treat the contract as at an end and take a full refund.
        </p>
        <p>Your order becomes your responsibility once it has been delivered to the address you gave us.</p>
      </Clause>

      <Clause n={6} title="Changing your mind">
        <p>
          <strong>You have ninety days to return anything, opened or unopened.</strong> Skincare has to be used to be judged, so getting
          halfway through a bottle and deciding it is not for you is a perfectly good reason to send it back. Email <Mail /> and we will
          arrange collection at our cost, then refund you to your original payment method.
        </p>
        <p>
          This is our own policy and it is deliberately more generous than the law requires. Your statutory right to cancel within fourteen
          days of receiving the goods, under the Consumer Contracts Regulations 2013, sits underneath it and is unaffected.
        </p>
        <p>
          We cannot accept back anything contaminated or damaged after delivery in a way that goes beyond normal handling. Sealed goods
          that are not suitable for return once unsealed for health protection reasons fall outside the statutory fourteen-day right once
          opened — but our ninety-day policy above still applies to them.
        </p>
      </Clause>

      <Clause n={7} title="If something is wrong">
        <p>
          Under the Consumer Rights Act 2015 what we sell you must be as described, of satisfactory quality and fit for purpose. If it is
          not, you are entitled to a repair, replacement or refund depending on the circumstances, and nothing on this page limits that.
        </p>
        <p>
          Tell us at <Mail /> with your order reference and, if it helps, a photograph. We would rather sort it out than argue about it.
        </p>
      </Clause>

      <Clause n={8} title="Using our products safely">
        <p>
          Our products are cosmetics, not medicines. Nothing on this site — including the skin quiz, the journal and any routine we suggest
          — is medical advice, and it does not replace a pharmacist, GP or dermatologist.
        </p>
        <p>
          Please read the label, follow the directions, and patch test before first use if your skin reacts easily. Stop using a product and
          seek advice if you get a reaction. If you are pregnant, breastfeeding or under the care of a dermatologist, check with them before
          starting anything new.
        </p>
      </Clause>

      <Clause n={9} title="Your account">
        <p>
          You can buy as a guest. If you create an account, keep your password to yourself and tell us at <Mail /> if you think someone else
          has it. You are responsible for orders placed through your account, unless someone used it without your knowledge and that was not
          your fault.
        </p>
        <p>We may suspend an account being used fraudulently or to abuse the site.</p>
      </Clause>

      <Clause n={10} title="Reviews and anything you post">
        <p>
          Reviews are read by a person before they appear, so there is a delay — that is moderation, not censorship. We publish critical
          reviews. We remove ones that are abusive, defamatory, off-topic, someone else&rsquo;s writing, or clearly not from a real customer.
        </p>
        <p>
          When you submit a review you keep ownership of what you wrote and give us permission to display it on this site and in our own
          marketing, alongside the first name and location you supplied. Ask us at <Mail /> and we will take it down.
        </p>
      </Clause>

      <Clause n={11} title="Our content">
        <p>
          The text, photography, formulation descriptions, design and code of this site belong to us or our licensors. You are welcome to
          read, print and share pages for your own use. You may not copy them for a commercial purpose, or use our name, branding or product
          photography to present yourself as connected to us, without our written permission.
        </p>
      </Clause>

      <Clause n={12} title="Availability of this site">
        <p>
          We do not promise the site will always be available or free of errors. We may change, suspend or withdraw parts of it without
          notice — though never in a way that affects an order you have already placed.
        </p>
      </Clause>

      <Clause n={13} title="Our responsibility to you">
        <p>
          If we fail to meet these terms, we are responsible for loss you suffer that is a foreseeable result of our breaking the contract or
          failing to use reasonable care. We are not responsible for loss that was not foreseeable when you ordered.
        </p>
        <p>
          <strong>We never exclude or limit</strong> our liability for death or personal injury caused by our negligence, for fraud or
          fraudulent misrepresentation, for defective products under the Consumer Protection Act 1987, or for anything else the law does not
          allow us to limit — including your rights under the Consumer Rights Act 2015.
        </p>
        <p>
          We supply our products for domestic and private use. If you use them for any commercial purpose, we are not liable for loss of
          profit, loss of business or business interruption.
        </p>
      </Clause>

      <Clause n={14} title="Things outside our control">
        <p>
          If something genuinely beyond our control delays or prevents us performing — a courier strike, extreme weather, the failure of a
          supplier we depend on — we will tell you and agree a new date, or refund you. We are not liable for the delay itself, but you can
          always cancel and take your money back if it drags on.
        </p>
      </Clause>

      <Clause n={15} title="Changes to these terms">
        <p>
          We may update these terms. The version that applies to your order is the one published when you placed it, and the date at the top
          of this page says when it last changed. Changes are never applied backwards to an order already made.
        </p>
      </Clause>

      <Clause n={16} title="Law and where to complain">
        <p>
          These terms are governed by the law of England and Wales, and the courts there can hear any dispute. If you live in Scotland or
          Northern Ireland you can also bring proceedings in your own courts, and you keep the consumer protections of the country you live
          in.
        </p>
        <p>
          Please come to us first at <Mail /> — most things are fixable. If we cannot resolve it between us, you can escalate to an
          alternative dispute resolution scheme or to your local Trading Standards service.
        </p>
        <p className="small">
          See also our <Link to="/privacy">Privacy Policy</Link>, which explains what we do with your information.
        </p>
      </Clause>
    </LegalShell>
  )
}

/* ----------------------------------------------------------------- privacy */

const PRIVACY_SECTIONS = [
  'Who is responsible',
  'What we collect',
  'Why we use it, and on what basis',
  'Payments',
  'Cookies and browser storage',
  'Who we share it with',
  'Where your data is held',
  'How long we keep it',
  'How we protect it',
  'Marketing emails',
  'Your rights',
  'Children',
  'Changes to this policy',
  'Contact and complaints',
]

export function Privacy() {
  return (
    <LegalShell
      title="Privacy Policy"
      summary="What we collect, why, and what you can do about it. We sell skincare; we do not sell data, run advertising trackers, or build profiles on you."
      sections={PRIVACY_SECTIONS}
    >
      <Clause n={1} title="Who is responsible">
        <p>
          {COMPANY.legalName}, trading as {COMPANY.tradingName}, is the data controller for the information described here. That means we
          decide what is collected and why, and we are accountable for it under UK GDPR and the Data Protection Act 2018.
        </p>
        <CompanyDetails />
      </Clause>

      <Clause n={2} title="What we collect">
        <p>Only what the shop needs to work. In practice that is:</p>

        <h3>When you order</h3>
        <ul>
          <li>Your name, email address and delivery address, plus your phone number and delivery notes if you give them.</li>
          <li>What you bought, what you paid, and the status of the order.</li>
        </ul>

        <h3>When you create an account</h3>
        <ul>
          <li>Your name and email address, and your password stored only as a bcrypt hash — we cannot read it, even if we wanted to.</li>
        </ul>

        <h3>When you use the rest of the site</h3>
        <ul>
          <li>Your email address, if you subscribe to our newsletter.</li>
          <li>Your answers and the routine we suggested, if you take the skin quiz, plus your email if you asked us to send it.</li>
          <li>The first name, location, rating and text of any review you submit.</li>
          <li>Your IP address, briefly and in memory only, so we can rate-limit abusive traffic. It is not written to our database.</li>
        </ul>

        <p>
          We do not collect special category data, we do not ask for your date of birth, and we never receive your card details. There is no
          analytics tag, advertising pixel or session-recording script anywhere on this site.
        </p>
      </Clause>

      <Clause n={3} title="Why we use it, and on what basis">
        <table className="legal-table">
          <thead>
            <tr>
              <th>What for</th>
              <th>What we use</th>
              <th>Lawful basis</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td data-label="What for">Taking, delivering and refunding your order</td>
              <td data-label="What we use">Name, contact details, address, order contents</td>
              <td data-label="Basis">Performing our contract with you</td>
            </tr>
            <tr>
              <td data-label="What for">Running your account and showing your order history</td>
              <td data-label="What we use">Name, email, password hash, orders</td>
              <td data-label="Basis">Performing our contract with you</td>
            </tr>
            <tr>
              <td data-label="What for">Answering your emails and handling returns</td>
              <td data-label="What we use">Whatever you tell us, plus the order</td>
              <td data-label="Basis">Our contract, and our legitimate interest in supporting customers</td>
            </tr>
            <tr>
              <td data-label="What for">Newsletters and offers</td>
              <td data-label="What we use">Email address</td>
              <td data-label="Basis">Your consent, which you can withdraw at any time</td>
            </tr>
            <tr>
              <td data-label="What for">Publishing your review</td>
              <td data-label="What we use">First name, location, rating, review text</td>
              <td data-label="Basis">Your consent, given when you submit it</td>
            </tr>
            <tr>
              <td data-label="What for">Suggesting a routine from the skin quiz</td>
              <td data-label="What we use">Your answers, and your email if supplied</td>
              <td data-label="Basis">Your consent</td>
            </tr>
            <tr>
              <td data-label="What for">Keeping the site secure and available</td>
              <td data-label="What we use">IP address, in memory, briefly</td>
              <td data-label="Basis">Our legitimate interest in preventing abuse and fraud</td>
            </tr>
            <tr>
              <td data-label="What for">Tax, accounting and consumer-law records</td>
              <td data-label="What we use">Order and payment records</td>
              <td data-label="Basis">Complying with our legal obligations</td>
            </tr>
          </tbody>
        </table>
        <p>
          Where we rely on legitimate interests, we have considered whether it is fair to you, and you can object — see{' '}
          <a href="#s11">your rights</a> below.
        </p>
      </Clause>

      <Clause n={4} title="Payments">
        <p>
          Card payments are processed by Stripe. You enter your card details on Stripe&rsquo;s own hosted payment page and they are sent to
          Stripe — never to us, and never through our servers. We are not able to see or store a card number.
        </p>
        <p>
          Stripe is a separate data controller for the payment itself, covered by{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer noopener">
            Stripe&rsquo;s privacy policy
          </a>
          . What comes back to us is your order reference, whether payment succeeded, and an identifier we can quote to Stripe if you need a
          refund.
        </p>
      </Clause>

      <Clause n={5} title="Cookies and browser storage">
        <p>
          <strong>We use one cookie.</strong> It is called <code>lumera_token</code>, it is set only when you sign in, it keeps you signed in
          for seven days, and it is signed so it cannot be tampered with. It is strictly necessary for the account area to work, which is why
          it does not need a consent banner. Signing out deletes it.
        </p>
        <p>
          Your shopping bag is kept in your browser&rsquo;s local storage — not in a cookie, and not on our servers — so an abandoned bag is
          still there when you come back. It never leaves your device until you check out. Clearing your site data clears it.
        </p>
        <p>
          We load our typefaces from Google Fonts, which means your browser requests them from Google and Google therefore sees your IP
          address. We mention it because it is the only third-party request a page on this site makes before you decide to buy anything.
        </p>
      </Clause>

      <Clause n={6} title="Who we share it with">
        <p>We do not sell your data, rent it, or hand it to advertisers. It goes to a short list of suppliers who help us run the shop:</p>
        <ul>
          <li>
            <strong>Stripe</strong> — to take payment and process refunds.
          </li>
          <li>
            <strong>Cloudflare</strong> — hosts this site, its database and its images, as our processor.
          </li>
          <li>
            <strong>Delivery partners</strong> — given your name, address and phone number only so they can deliver your parcel.
          </li>
        </ul>
        <p>
          We will also disclose information where the law requires it, or to establish or defend a legal claim. If the business is ever sold,
          customer records may transfer to the buyer, who would be bound by this policy until they told you otherwise.
        </p>
      </Clause>

      <Clause n={7} title="Where your data is held">
        <p>
          Our database and images are stored on Cloudflare&rsquo;s infrastructure. Cloudflare and Stripe both operate globally, so your data
          may be processed outside the UK. Where it is, those transfers are covered by the UK&rsquo;s international data transfer agreement,
          or the UK addendum to the EU standard contractual clauses, which contractually holds the recipient to UK-equivalent protection.
        </p>
      </Clause>

      <Clause n={8} title="How long we keep it">
        <ul>
          <li>
            <strong>Orders and payment records</strong> — six years after the order, which is what tax law requires.
          </li>
          <li>
            <strong>Your account</strong> — until you ask us to close it. Orders already placed are kept for the period above.
          </li>
          <li>
            <strong>Newsletter subscription</strong> — until you unsubscribe.
          </li>
          <li>
            <strong>Reviews</strong> — until you or we take them down.
          </li>
          <li>
            <strong>Quiz answers</strong> — up to two years, so we can see which routines we are recommending.
          </li>
          <li>
            <strong>Rate-limiting records</strong> — minutes. They live in memory and are gone when the server restarts.
          </li>
        </ul>
      </Clause>

      <Clause n={9} title="How we protect it">
        <p>
          The whole site is served over HTTPS. Passwords are hashed with bcrypt at cost 12 and are never stored or logged in readable form.
          Session cookies are signed, HTTP-only and restricted to this site. Card data never reaches us at all. Admin access is restricted to
          named accounts, and our error handling is written so internal details are never returned to a browser.
        </p>
        <p>No system is perfectly secure. If a breach ever put your rights at risk, we would tell you, and the ICO, as the law requires.</p>
      </Clause>

      <Clause n={10} title="Marketing emails">
        <p>
          We email you about new launches and offers only if you asked us to. Every email has an unsubscribe link, and you can also email{' '}
          <Mail /> to be removed. Withdrawing consent does not affect emails already sent, and we will still send the transactional messages
          an order requires — confirmations, dispatch notices and refund notifications.
        </p>
      </Clause>

      <Clause n={11} title="Your rights">
        <p>Under UK data protection law you can ask us to:</p>
        <ul>
          <li>
            <strong>Give you a copy</strong> of the personal data we hold about you.
          </li>
          <li>
            <strong>Correct</strong> anything wrong or incomplete.
          </li>
          <li>
            <strong>Delete</strong> it, where we have no legal reason to keep it — order records we must keep for tax are the usual
            exception.
          </li>
          <li>
            <strong>Restrict or object</strong> to how we use it, including anything we do on the basis of legitimate interests.
          </li>
          <li>
            <strong>Port it</strong> — receive it in a machine-readable format, or have it sent to another provider.
          </li>
          <li>
            <strong>Withdraw consent</strong> at any time, where consent is what we relied on.
          </li>
        </ul>
        <p>
          Email <Mail /> and we will respond within one month. It is free, and we will not ask you to justify the request — though we may
          need to check you are who you say you are before sending personal data anywhere.
        </p>
        <p>We do not make any automated decision about you that produces a legal or similarly significant effect.</p>
      </Clause>

      <Clause n={12} title="Children">
        <p>
          This shop is not intended for children and we do not knowingly collect data from anyone under 16. If you believe a child has given
          us their details, email <Mail /> and we will delete them.
        </p>
      </Clause>

      <Clause n={13} title="Changes to this policy">
        <p>
          We will update this page when what we do changes, and the date at the top shows when it last did. If a change materially affects
          your rights, we will tell subscribers and account holders by email rather than quietly editing the page.
        </p>
      </Clause>

      <Clause n={14} title="Contact and complaints">
        <p>
          For anything in this policy, email <Mail />. We would rather hear from you directly, and most questions are answered the same week.
        </p>
        <p>
          You also have the right to complain to the Information Commissioner&rsquo;s Office, the UK&rsquo;s data protection regulator, at{' '}
          <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer noopener">
            ico.org.uk
          </a>{' '}
          or on 0303 123 1113. Doing so does not affect any other right you have.
        </p>
        <p>
          You can also reach us on {SOCIALS.map((s) => s.label).join(', ')} — but please do not send personal details or order information
          through a social message. Those are handled by the platform under its own privacy terms, not by us. Email <Mail /> instead.
        </p>
        <p className="small">
          See also our <Link to="/terms">Terms &amp; Conditions</Link>.
        </p>
      </Clause>
    </LegalShell>
  )
}
